import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getPublicQRCodes } from "../api/qrcode";
import toast from "react-hot-toast";
import { IoClose } from "react-icons/io5";

const ImageVideosInfoPage = () => {
  const { id } = useParams();

  const [processing, setProcessing] = useState(false);
  const [barcodeData, setBarcodeData] = useState(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);

  const getBarcodeData = async () => {
    setProcessing(true);
    try {
      const { data } = await getPublicQRCodes(id);
      setBarcodeData(data.barcode);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error while getting barcode data"
      );
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    getBarcodeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (barcodeData?.storedInfo?.imageVideos) {
      const newImages = [];
      const newVideos = [];

      barcodeData.storedInfo.imageVideos.forEach((item) => {
        if (
          [".mp4", ".mov", ".avi", ".wmv", ".flv", ".mkv"].some((extension) =>
            item.endsWith(extension)
          )
        ) {
          newVideos.push(item);
        } else {
          newImages.push(item);
        }
      });

      setImages(newImages);
      setVideos(newVideos);
    }
  }, [barcodeData]);

  return (
    <>
      <div className="">
        <div className="w-full max-w-4xl p-4 md:p-8 mx-auto mt-5">
          {/* Images Section */}
          <div className="group">
            {/* Images Carousel */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images?.map((image, index) => {
                return (
                  <div
                    key={index}
                    className="overflow-hidden  h-[156px] xs:h-[245px] rounded-md"
                  >
                    <img
                      src={image}
                      alt={`Memory ${index + 1}`}
                      className="w-full h-full object-cover cursor-pointer rounded-md transition-transform duration-300 hover:scale-110"
                      onClick={() => setSelectedImage(image)}
                    />
                  </div>
                );
              })}{" "}
            </div>

            {/* Image Pop-out */}
            {selectedImage && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
                onClick={() => setSelectedImage(null)}
              >
                <div className="relative flex items-center justify-center h-[90vh] w-full">
                  <div className="relative inline-block">
                    <img
                      src={selectedImage}
                      alt="Enlarged view"
                      className="max-w-full max-h-[90vh] object-contain rounded"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(null);
                      }}
                      className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full text-xl w-7 h-7 flex items-center justify-center leading-none hover:bg-opacity-75 transition-opacity"
                    >
                      <IoClose />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="sm:mt-12 mt-7">
            <div className="space-y-4 flex flex-col sm:gap-5 gap-2">
              {videos.map((video, index) => {
                return (
                  <div className="flex justify-center">
                    {/* Check if video url contains 'youtube.com' or 'youtu.be' */}
                    {video.includes("youtube.com") ||
                    video.includes("youtu.be") ? (
                      <iframe
                        className="w-full rounded-md h-[300px] md:h-[400px]"
                        src={`https://www.youtube.com/embed/${
                          video?.split("/")[3]
                        }`}
                        title={`YouTube video player for ${video}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        className="w-full h-auto md:h-[400px] rounded-md"
                        controls
                        loop
                        playsInline
                        type={`video/${video?.split(".").pop()}`}
                      >
                        <source src={`${video}#t=0.001`} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {images?.length === 0 && videos?.length === 0 && !processing && (
          <div className="flex flex-col items-center justify-center h-64 text-center px-4">
            <svg
              className="w-16 h-16 text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              ></path>
            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No Media Found
            </h3>
          </div>
        )}

        {/* Loading State */}
        {processing ? (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
            <div
              className="h-10 w-10 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"
              role="status"
            >
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default ImageVideosInfoPage;
